import React, { useState, useMemo } from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import Loading from "../Loading";
import ProjectCard from "./ProjectCards";
import Particle from "../Particle";
import { useTranslation } from "react-i18next";
import { useData } from "../../context/DataContext";
import { getImageURL } from "../../api";

function Projects() {
  const { projects, loading } = useData();
  const { t, i18n } = useTranslation();
  const lang = i18n.language.split("-")[0];
  const [selectedCategory, setSelectedCategory] = useState("All");

  // Get unique categories
  const categories = useMemo(() => {
    const cats = [
      "All",
      ...new Set(projects.map((p) => p.category).filter(Boolean)),
    ];
    return cats;
  }, [projects]);

  // Filter projects by category
  const filteredProjects = useMemo(() => {
    if (selectedCategory === "All") {
      return projects;
    }
    return projects.filter((p) => p.category === selectedCategory);
  }, [projects, selectedCategory]);

  if (loading) {
    return <Loading message="Loading Projects..." />;
  }

  return (
    <Container fluid className="project-section">
      <Particle />
      <Container>
        <h1 className="project-heading">
          {t("my_recent", "My Recent")}{" "}
          <strong className="purple">{t("works", "Works")} </strong>
        </h1>
        <p style={{ color: "white" }}>
          {t(
            "projects_subtitle",
            "Here are a few projects I've worked on recently."
          )}
        </p>

        {/* Category Filter */}
        {categories.length > 1 && (
          <Row className="justify-content-center mb-4">
            <Col md={10} className="text-center">
              <div className="d-flex flex-wrap justify-content-center gap-2">
                {categories.map((cat) => (
                  <Button
                    key={cat}
                    variant={
                      selectedCategory === cat ? "primary" : "outline-primary"
                    }
                    onClick={() => setSelectedCategory(cat)}
                    style={{
                      backgroundColor:
                        selectedCategory === cat ? "#c770f0" : "transparent",
                      borderColor: "#c770f0",
                      color: selectedCategory === cat ? "#fff" : "#c770f0",
                      fontWeight: "600",
                      borderRadius: "8px",
                      padding: "8px 16px",
                      margin: "2px",
                      minWidth: "80px",
                      fontSize: "14px",
                    }}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
            </Col>
          </Row>
        )}

        <Row style={{ justifyContent: "center", paddingBottom: "10px" }}>
          {filteredProjects
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((project) => {
              // Rasmlar uchun URL yaratish (support both image and imageUrl)
              const imageField = project.image || project.imageUrl;
              let imageUrl;
              try {
                imageUrl = getImageURL(imageField);
                // Agar URL yaratilmagan bo'lsa yoki noto'g'ri bo'lsa, placeholder ishlatamiz
                if (!imageUrl || !imageUrl.startsWith("http")) {
                  imageUrl =
                    "https://via.placeholder.com/300x200?text=Project+Image";
                }
              } catch (error) {
                console.warn(
                  "Image URL error for project:",
                  project.title,
                  error
                );
                imageUrl =
                  "https://via.placeholder.com/300x200?text=Project+Image";
              }

              return (
                <Col md={4} className="project-card" key={project._id}>
                  <ProjectCard
                    id={project._id}
                    imgPath={imageUrl}
                    isBlog={false}
                    title={project.title[lang]}
                    description={project.description[lang]}
                    category={project.category}
                    techStack={project.techStack}
                    ghLink={project.githubLink}
                    demoLink={project.demoUrl}
                    liveLink={project.liveLink}
                  />
                </Col>
              );
            })}
          {filteredProjects.length === 0 && (
            <p style={{ color: "white" }}>
              {selectedCategory === "All"
                ? t("no_projects", "No projects found yet.")
                : `No projects in ${selectedCategory} category.`}
            </p>
          )}
        </Row>
      </Container>
    </Container>
  );
}

export default Projects;
